import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { useCart } from '../contexts/CartContext';
import { describeLine } from '../lib/cartLine';
import { useAuth } from '../contexts/AuthContext';
import { Tag, Lock, CheckCircle } from 'lucide-react';
import Select from '../components/FlatSelect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Checkout() {
  const { cart, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [address, setAddress] = useState({
    name: user?.name || '', line1: '', line2: '', city: '', state: '', postal_code: '', country: 'US', phone: ''
  });
  const [quote, setQuote] = useState(null);
  const [appliedCoupon, setAppliedCoupon] = useState('');

  const { items, subtotal } = cart;
  const tax = quote?.tax ?? 0;
  const shipping = quote?.shipping ?? 0;
  const total = quote?.total ?? subtotal;

  const fetchQuote = async (code = appliedCoupon) => {
    const r = await axios.get(`${API}/checkout/quote`, { params: { coupon_code: code || '' }, withCredentials: true });
    setQuote(r.data);
    return r.data;
  };

  useEffect(() => { if (items.length) fetchQuote().catch(() => {}); }, [cart]);

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponError('');
    try {
      const q = await fetchQuote(couponCode);
      if (q.coupon_error) {
        setCouponError(q.coupon_error);
        setCouponDiscount(0);
        setAppliedCoupon('');
      } else {
        setCouponDiscount(q.discount);
        setAppliedCoupon(q.coupon_code || '');
      }
    } catch (e) {
      setCouponError(e.response?.data?.detail || 'Invalid coupon');
      setCouponDiscount(0);
    } finally { setCouponLoading(false); }
  };

  const handleAddressSubmit = (e) => {
    e.preventDefault();
    setStep(2);
  };

  const placeOrder = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.post(`${API}/orders`, {
        shipping_address: address,
        coupon_code: appliedCoupon || null,
        payment_source_id: null
      }, { withCredentials: true });
      setOrder(r.data);
      setStep(3);
    } catch (e) {
      setError(e.response?.data?.detail || 'Order failed. Please try again.');
    } finally { setLoading(false); }
  };

  if (items.length === 0 && step !== 3) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="store-page relative">
      <Navbar />
      <div className="relative z-10 max-w-5xl mx-auto px-6 pt-8 pb-20">
        <h1 className="text-3xl font-black text-ink mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {step === 3 ? 'Order Confirmed!' : 'Checkout'}
        </h1>

        {/* Steps */}
        {step !== 3 && (
          <div className="flex items-center gap-4 mb-8">
            {[1, 2].map(s => (
              <React.Fragment key={s}>
                <div className={`flex items-center gap-2 text-sm font-medium ${step >= s ? 'text-honey-700' : 'text-ink/45'}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step >= s ? 'bg-honey text-black' : 'bg-ink/[0.07] text-ink/45'}`}>{s}</div>
                  {s === 1 ? 'Shipping' : 'Review & Pay'}
                </div>
                {s < 2 && <div className={`flex-1 h-px ${step > s ? 'bg-honey/50' : 'bg-ink/[0.07]'}`} />}
              </React.Fragment>
            ))}
          </div>
        )}

        {step === 3 ? (
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-20">
            <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-black text-ink mb-2">Order Placed!</h2>
            <p className="text-ink/70 mb-2">Order #{order?.order_number}</p>
            <p className="text-ink/55 text-sm mb-8">Total: <span className="text-honey-700 font-bold">${order?.total?.toFixed(2)}</span></p>
            <div className="flex gap-4 justify-center">
              <button onClick={() => navigate('/orders')} className="btn-primary">View Orders</button>
              <button onClick={() => navigate('/')} className="btn-secondary">Continue Shopping</button>
            </div>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              {step === 1 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <div className="glass-card rounded-2xl p-6">
                    <h2 className="text-lg font-bold text-ink mb-4">Shipping Address</h2>
                    <form onSubmit={handleAddressSubmit} className="space-y-4">
                      <input type="text" value={address.name} onChange={e => setAddress({...address, name: e.target.value})}
                        placeholder="Full Name" required
                        className="w-full bg-ink/[0.04] border border-ink/10 rounded-xl px-4 py-3 text-ink placeholder-ink/40 focus:outline-none focus:border-honey transition-colors" />
                      <input type="text" value={address.line1} onChange={e => setAddress({...address, line1: e.target.value})}
                        placeholder="Street Address" required
                        className="w-full bg-ink/[0.04] border border-ink/10 rounded-xl px-4 py-3 text-ink placeholder-ink/40 focus:outline-none focus:border-honey transition-colors" />
                      <input type="text" value={address.line2} onChange={e => setAddress({...address, line2: e.target.value})}
                        placeholder="Apartment, suite, etc. (optional)"
                        className="w-full bg-ink/[0.04] border border-ink/10 rounded-xl px-4 py-3 text-ink placeholder-ink/40 focus:outline-none focus:border-honey transition-colors" />
                      <div className="grid grid-cols-2 gap-4">
                        <input type="text" value={address.city} onChange={e => setAddress({...address, city: e.target.value})}
                          placeholder="City" required
                          className="bg-ink/[0.04] border border-ink/10 rounded-xl px-4 py-3 text-ink placeholder-ink/40 focus:outline-none focus:border-honey transition-colors" />
                        <input type="text" value={address.state} onChange={e => setAddress({...address, state: e.target.value})}
                          placeholder="State" required
                          className="bg-ink/[0.04] border border-ink/10 rounded-xl px-4 py-3 text-ink placeholder-ink/40 focus:outline-none focus:border-honey transition-colors" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <input type="text" value={address.postal_code} onChange={e => setAddress({...address, postal_code: e.target.value})}
                          placeholder="ZIP Code" required
                          className="bg-ink/[0.04] border border-ink/10 rounded-xl px-4 py-3 text-ink placeholder-ink/40 focus:outline-none focus:border-honey transition-colors" />
                        <Select value={address.country} onChange={e => setAddress({...address, country: e.target.value})}
                          className="w-full bg-ink/[0.04] border border-ink/10 rounded-xl px-4 py-3 text-ink focus:outline-none focus:border-honey transition-colors appearance-none"
                          style={{ colorScheme: 'light' }}>
                          <option value="US" style={{ background: '#FFFFFF' }}>United States</option>
                          <option value="CA" style={{ background: '#FFFFFF' }}>Canada</option>
                          <option value="GB" style={{ background: '#FFFFFF' }}>United Kingdom</option>
                          <option value="AU" style={{ background: '#FFFFFF' }}>Australia</option>
                          <option value="DE" style={{ background: '#FFFFFF' }}>Germany</option>
                          <option value="FR" style={{ background: '#FFFFFF' }}>France</option>
                          <option value="JP" style={{ background: '#FFFFFF' }}>Japan</option>
                          <option value="MX" style={{ background: '#FFFFFF' }}>Mexico</option>
                        </Select>
                      </div>
                      <input type="tel" value={address.phone} onChange={e => setAddress({...address, phone: e.target.value})}
                        placeholder="Phone (optional, for delivery questions)"
                        className="w-full bg-ink/[0.04] border border-ink/10 rounded-xl px-4 py-3 text-ink placeholder-ink/40 focus:outline-none focus:border-honey transition-colors" />
                      <button type="submit" className="w-full btn-primary py-3" data-testid="address-continue-btn">
                        Continue to Review
                      </button>
                    </form>
                  </div>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
                  <div className="glass-card rounded-2xl p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-lg font-bold text-ink">Shipping To</h2>
                      <button onClick={() => setStep(1)} className="text-honey-700 text-sm">Edit</button>
                    </div>
                    <p className="text-ink/70 text-sm">{address.name}<br/>{address.line1}{address.line2 ? `, ${address.line2}` : ''}<br/>{address.city}, {address.state} {address.postal_code}</p>
                  </div>

                  <div className="glass-card rounded-2xl p-6">
                    <h2 className="text-lg font-bold text-ink mb-2 flex items-center gap-2">
                      <Lock className="w-4 h-4 text-honey-700" /> Payment
                    </h2>
                    <p className="text-ink/55 text-sm mb-4">Your order will be placed and we'll contact you for payment. Square integration requires credentials setup.</p>
                    {error && <p className="text-red-600 text-sm mb-4 p-3 bg-red-500/10 rounded-xl">{error}</p>}
                    <button onClick={placeOrder} disabled={loading}
                      className="w-full btn-primary py-3 flex items-center justify-center gap-2" data-testid="place-order-btn">
                      {loading ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> : null}
                      {loading ? 'Placing Order...' : `Place Order - $${total.toFixed(2)}`}
                    </button>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Summary */}
            <div className="glass-card rounded-2xl p-6 h-fit">
              <h3 className="text-lg font-bold text-ink mb-4">Order Summary</h3>
              <div className="space-y-3 mb-4">
                {items.map(item => (
                  <div key={item.line_id} className="flex gap-3 items-start">
                    <div className="w-12 h-12 bg-ink/[0.04] rounded-lg overflow-hidden flex-shrink-0">
                      {item.image && <img src={item.image} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-ink text-xs truncate">{item.title}</p>
                      {describeLine(item) && <p className="text-ink/55 text-xs">{describeLine(item)}</p>}
                      <p className="text-ink/55 text-xs">x{item.quantity}</p>
                    </div>
                    <p className="text-ink text-sm font-bold">${(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                ))}
              </div>

              {/* Coupon */}
              <div className="border-t border-ink/10 pt-4 mb-4">
                <div className="flex gap-2 mb-2">
                  <input type="text" value={couponCode} onChange={e => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="Coupon code" data-testid="coupon-input"
                    className="flex-1 bg-ink/[0.04] border border-ink/10 rounded-xl px-3 py-2 text-ink text-sm placeholder-ink/40 focus:outline-none focus:border-honey transition-colors" />
                  <button onClick={applyCoupon} disabled={couponLoading}
                    data-testid="apply-coupon-btn"
                    className="px-3 py-2 bg-honey text-black rounded-xl text-sm font-bold hover:bg-honey-600 transition-colors disabled:opacity-50">
                    <Tag className="w-4 h-4" />
                  </button>
                </div>
                {couponError && <p className="text-red-600 text-xs">{couponError}</p>}
                {couponDiscount > 0 && <p className="text-green-600 text-xs">Discount: -${couponDiscount.toFixed(2)}</p>}
              </div>

              <div className="space-y-2 text-sm border-t border-ink/10 pt-4">
                <div className="flex justify-between text-ink/70"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between text-ink/70"><span>Tax{quote?.tax_rate ? ` (${quote.tax_rate}%)` : ''}</span><span>${tax.toFixed(2)}</span></div>
                <div className="flex justify-between text-ink/70"><span>Shipping</span><span>{shipping === 0 ? <span className="text-green-600">Free</span> : `$${shipping.toFixed(2)}`}</span></div>
                {(quote?.discounts || []).map((d, i) => (
                  <div key={i} className="flex justify-between text-green-600"><span>{d.name}{d.sets > 1 ? ` ×${d.sets}` : ''}</span><span>-${d.amount.toFixed(2)}</span></div>
                ))}
                {quote?.free_shipping_coupon && <p className="text-green-600 text-xs">Free shipping with {quote.coupon_code}</p>}
                <div className="border-t border-ink/10 pt-2 flex justify-between text-ink font-bold"><span>Total</span><span className="text-honey-700">${total.toFixed(2)}</span></div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
